'use client';

import * as React from 'react';
import { useSearchParams } from 'next/navigation';
import { getDictionary } from '@/lib/i18n';

type AnyDict = Record<string, any>;

type I18nCtx = {
  locale: 'es' | 'fr' | 'en' | 'ar';
  dir: 'ltr' | 'rtl';
  t: (key: string) => string;
  dict: AnyDict; // ← AGREGADO: exponer el diccionario
  setLocale: (loc: I18nCtx['locale']) => Promise<void>;
  ready: boolean;
};

const Ctx = React.createContext<I18nCtx | null>(null);

function readCookie(name: string) {
  if (typeof document === 'undefined') return '';
  const m = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
  return m ? decodeURIComponent(m[2]) : '';
}

function normLocale(v: any): I18nCtx['locale'] {
  const s = String(v || '').toLowerCase();
  if (s.startsWith('fr')) return 'fr';
  if (s.startsWith('en')) return 'en';
  if (s.startsWith('ar')) return 'ar';
  return 'es';
}

function getByPath(obj: any, path: string) {
  if (!obj) return undefined;
  if (typeof obj[path] === 'string') return obj[path];
  return path.split('.').reduce((acc, k) => (acc && acc[k] != null ? acc[k] : undefined), obj);
}

async function apiGet(path: string) {
  const res = await fetch(path, { method: 'GET', cache: 'no-store', credentials: 'include' });
  const json = await res.json().catch(() => ({}));
  if (!res.ok || json?.ok === false) throw new Error(json?.error || 'request_failed');
  return json;
}

async function apiPut(path: string, body: any) {
  const res = await fetch(path, {
    method: 'PUT',
    cache: 'no-store',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok || json?.ok === false) throw new Error(json?.error || 'request_failed');
  return json;
}

export function I18nProvider({ children }: { children: React.ReactNode }) {
  const params = useSearchParams();
  const tenantFromUrl = params.get('tenant') || '';

  const [tenantId, setTenantId] = React.useState<string>(tenantFromUrl);
  const [locale, _setLocale] = React.useState<I18nCtx['locale']>('es');

  const [dict, setDict] = React.useState<AnyDict>({});
  const [fallback, setFallback] = React.useState<AnyDict>({});
  const [ready, setReady] = React.useState(false);

  const dir: I18nCtx['dir'] = locale === 'ar' ? 'rtl' : 'ltr';

  const applyToDom = React.useCallback((loc: I18nCtx['locale']) => {
    if (typeof document === 'undefined') return;
    document.documentElement.lang = loc;
    document.documentElement.dir = loc === 'ar' ? 'rtl' : 'ltr';
  }, []);

  React.useEffect(() => {
    let alive = true;

    if (tenantFromUrl) {
      setTenantId(tenantFromUrl);
      return;
    }

    (async () => {
      try {
        const me = await apiGet('/api/me');
        const t = String(me?.tenant_id || me?.user?.tenant_id || '');
        if (alive && t) setTenantId(t);
      } catch {
        // si falla, tenantId queda vacío y seguimos con local/cookie
      }
    })();

    return () => { alive = false; };
  }, [tenantFromUrl]);

  const persistLocal = React.useCallback((loc: I18nCtx['locale']) => {
    try { localStorage.setItem('zepelin_locale', loc); } catch {}
    try { document.cookie = `zepelin_locale=${encodeURIComponent(loc)}; path=/; max-age=${60 * 60 * 24 * 365}`; } catch {}
  }, []);

  const setLocale = React.useCallback(async (loc: I18nCtx['locale']) => {
    const n = normLocale(loc);

    _setLocale(n);
    applyToDom(n);
    persistLocal(n);

    try {
      if (tenantId) {
        const qs = `?tenant=${encodeURIComponent(tenantId)}`;
        await apiPut(`/api/settings/ui${qs}`, { language: n, locale: n });
      }
    } catch {
      // si falla DB, UI sigue con localStorage/cookie
    }
  }, [applyToDom, persistLocal, tenantId]);

  React.useEffect(() => {
    const qLang = params.get('lang');
    const ls = (() => { try { return localStorage.getItem('zepelin_locale') || ''; } catch { return ''; } })();
    const ck = readCookie('zepelin_locale');

    const initial = normLocale(qLang || ls || ck || navigator.language || 'es');
    _setLocale(initial);
    applyToDom(initial);
    persistLocal(initial);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    let alive = true;

    (async () => {
      try {
        const fb = await getDictionary('es');
        if (alive) setFallback(fb || {});
      } catch {}

      try {
        if (tenantId) {
          const r = await apiGet(`/api/settings/ui?tenant=${encodeURIComponent(tenantId)}`);
          const serverLoc = normLocale(r?.language || r?.locale);
          if (alive) {
            _setLocale(serverLoc);
            applyToDom(serverLoc);
            persistLocal(serverLoc);
          }
        }
      } catch {}

      if (alive) setReady(true);
    })();

    return () => { alive = false; };
  }, [tenantId, applyToDom, persistLocal]);

  React.useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const d = await getDictionary(locale);
        if (alive) setDict(d || {});
      } catch {
        if (alive) setDict({});
      }
    })();
    return () => { alive = false; };
  }, [locale]);

  const t = React.useCallback((key: string) => {
    const v1 = getByPath(dict, key);
    if (typeof v1 === 'string') return v1;
    const v2 = getByPath(fallback, key);
    if (typeof v2 === 'string') return v2;
    return key;
  }, [dict, fallback]);

  // ✅ AGREGADO: exponer dict en el contexto
  const value: I18nCtx = React.useMemo(
    () => ({ locale, dir, t, dict, setLocale, ready }),
    [locale, dir, t, dict, setLocale, ready]
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useI18n() {
  const v = React.useContext(Ctx);
  if (!v) throw new Error('useI18n must be used inside <I18nProvider />');
  return v;
}
